import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const articles = [
  {
    id: 1,
    category: 'Leadership',
    title: 'Mon parcours de Tech Lead : Diriger une équipe de développeurs',
    excerpt: 'Comment j\'ai appris à gérer une équipe technique, les défis rencontrés et les leçons apprises en tant que Tech Lead dans différentes entreprises.',
    date: '15 Jan 2025',
    readTime: '8 min',
    image: 'https://readdy.ai/api/search-image?query=modern%20tech%20team%20meeting%20in%20office%20with%20diverse%20developers%20collaborating%20on%20project%20whiteboard%20with%20diagrams%20professional%20corporate%20environment%20warm%20lighting&width=600&height=400&seq=blog1&orientation=landscape',
    tags: ['Leadership', 'Management', 'Tech Lead'],
    featured: true
  },
  {
    id: 2,
    category: 'Architecture',
    title: 'Clean Architecture avec Node.js et TypeScript',
    excerpt: 'Guide complet pour implémenter une architecture propre et maintenable dans vos projets backend avec les principes SOLID.',
    date: '10 Jan 2025',
    readTime: '12 min',
    image: 'https://readdy.ai/api/search-image?query=abstract%20software%20architecture%20diagram%20with%20clean%20geometric%20shapes%20nodes%20and%20connections%20modern%20minimalist%20design%20dark%20background%20with%20blue%20accents&width=600&height=400&seq=blog2&orientation=landscape',
    tags: ['Architecture', 'Node.js', 'TypeScript', 'SOLID'],
    featured: false
  },
  {
    id: 3,
    category: 'DevOps',
    title: 'CI/CD avec GitLab et Kubernetes : Retour d\'expérience',
    excerpt: 'Comment j\'ai mis en place des pipelines CI/CD robustes pour automatiser les déploiements et améliorer la productivité de l\'équipe.',
    date: '5 Jan 2025',
    readTime: '10 min',
    image: 'https://readdy.ai/api/search-image?query=devops%20pipeline%20visualization%20with%20containers%20kubernetes%20pods%20deployment%20workflow%20modern%20tech%20infrastructure%20dark%20theme%20with%20orange%20and%20blue%20highlights&width=600&height=400&seq=blog3&orientation=landscape',
    tags: ['DevOps', 'CI/CD', 'Kubernetes', 'GitLab'],
    featured: false
  },
  {
    id: 4,
    category: 'Management',
    title: 'Chef de Département IT : Gérer les projets et les équipes',
    excerpt: 'Les responsabilités d\'un chef de département IT, la gestion des budgets, des ressources humaines et la coordination des projets stratégiques.',
    date: '28 Déc 2024',
    readTime: '15 min',
    image: 'https://readdy.ai/api/search-image?query=executive%20IT%20manager%20in%20modern%20office%20reviewing%20project%20dashboard%20on%20large%20screen%20strategic%20planning%20meeting%20professional%20corporate%20setting&width=600&height=400&seq=blog4&orientation=landscape',
    tags: ['Management', 'IT', 'Stratégie', 'Leadership'],
    featured: true
  },
  {
    id: 5,
    category: 'Backend',
    title: 'Optimisation des API REST avec Redis et Caching',
    excerpt: 'Techniques avancées pour améliorer les performances de vos API avec la mise en cache Redis et les bonnes pratiques de conception.',
    date: '20 Déc 2024',
    readTime: '9 min',
    image: 'https://readdy.ai/api/search-image?query=database%20optimization%20visualization%20with%20redis%20cache%20nodes%20data%20flow%20diagram%20modern%20tech%20aesthetic%20dark%20background%20with%20red%20accents&width=600&height=400&seq=blog5&orientation=landscape',
    tags: ['Backend', 'Redis', 'API', 'Performance'],
    featured: false
  },
  {
    id: 6,
    category: 'Mobile',
    title: 'Flutter : Développer des applications cross-platform performantes',
    excerpt: 'Mon expérience avec Flutter pour créer des applications mobiles natives pour iOS et Android avec une seule base de code.',
    date: '15 Déc 2024',
    readTime: '11 min',
    image: 'https://readdy.ai/api/search-image?query=mobile%20app%20development%20flutter%20framework%20smartphone%20mockups%20with%20beautiful%20UI%20design%20modern%20minimalist%20style%20gradient%20background&width=600&height=400&seq=blog6&orientation=landscape',
    tags: ['Mobile', 'Flutter', 'Dart', 'Cross-platform'],
    featured: false
  }
];

const categories = ['Tous', 'Leadership', 'Architecture', 'DevOps', 'Management', 'Backend', 'Mobile'];

export default function BlogPage() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('Tous');
  const [hoveredArticle, setHoveredArticle] = useState<number | null>(null);

  const filteredArticles = activeCategory === 'Tous' 
    ? articles 
    : articles.filter(article => article.category === activeCategory);

  const featuredArticles = articles.filter(article => article.featured);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <section className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-[#0066FF]/10 text-[#0066FF] px-4 py-2 rounded-full text-sm font-medium mb-6">
              <i className="ri-article-line"></i>
              Blog & Articles
            </div>
            <h2 className="text-5xl font-bold text-[#0A0A0A] mb-6">
              Partage d'<span className="text-[#0066FF]">Expertise</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Articles techniques, retours d'expérience en leadership et conseils issus de mon parcours 
              en tant que Tech Lead et Chef de Département IT.
            </p>
          </div>

          {/* Leadership Banner */}
          <div className="bg-gradient-to-r from-[#0A0A0A] to-[#1a1a1a] rounded-3xl p-8 mb-16 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-1/2 h-full opacity-10">
              <img 
                src="https://readdy.ai/api/search-image?query=abstract%20leadership%20concept%20with%20geometric%20shapes%20arrows%20pointing%20upward%20success%20growth%20visualization%20modern%20minimalist%20style&width=600&height=400&seq=leaderbanner&orientation=landscape"
                alt="Leadership" loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="relative z-10 grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-[#0066FF]/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-team-line text-3xl text-[#0066FF]"></i>
                </div>
                <div className="text-3xl font-bold text-white mb-2">Tech Lead</div>
                <div className="text-gray-400 text-sm">Direction d'équipes techniques</div>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-[#0066FF]/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-building-line text-3xl text-[#0066FF]"></i>
                </div>
                <div className="text-3xl font-bold text-white mb-2">Chef Dept. IT</div>
                <div className="text-gray-400 text-sm">Gestion de département</div>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-[#0066FF]/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-git-merge-line text-3xl text-[#0066FF]"></i>
                </div>
                <div className="text-3xl font-bold text-white mb-2">3+ Entreprises</div>
                <div className="text-gray-400 text-sm">Expériences variées</div>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-[#0066FF]/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-user-star-line text-3xl text-[#0066FF]"></i>
                </div>
                <div className="text-3xl font-bold text-white mb-2">10+ Devs</div>
                <div className="text-gray-400 text-sm">Équipes dirigées</div>
              </div>
            </div>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  activeCategory === category
                    ? 'bg-[#0066FF] text-white shadow-lg'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Featured Articles */}
          {activeCategory === 'Tous' && (
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {featuredArticles.map((article) => (
                <div
                  key={article.id}
                  className="group relative bg-[#0A0A0A] rounded-3xl overflow-hidden cursor-pointer"
                  onMouseEnter={() => setHoveredArticle(article.id)}
                  onMouseLeave={() => setHoveredArticle(null)}
                  onClick={() => navigate(`/blog/${article.id}`)}
                >
                  <div className="absolute inset-0">
                    <img
                      src={article.image}
                      alt={article.title}
                      loading="lazy"
                      className="w-full h-full object-cover opacity-60 group-hover:opacity-40 group-hover:scale-110 transition-all duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
                  </div>
                  <div className="relative z-10 p-8 h-full flex flex-col justify-end min-h-[400px]">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="bg-[#0066FF] text-white px-3 py-1 rounded-full text-xs font-medium">
                        {article.category}
                      </span>
                      <span className="bg-yellow-500 text-black px-3 py-1 rounded-full text-xs font-medium">
                        Featured
                      </span>
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-[#0066FF] transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-gray-300 mb-4 line-clamp-2">{article.excerpt}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 text-gray-400 text-sm">
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line"></i>
                          {article.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i>
                          {article.readTime}
                        </span>
                      </div>
                      <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center group-hover:bg-[#0066FF] transition-colors">
                        <i className="ri-arrow-right-up-line text-white"></i>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Articles Grid */}
          <div className="grid md:grid-cols-3 gap-8">
            {filteredArticles.filter(a => activeCategory !== 'Tous' || !a.featured).map((article) => (
              <div
                key={article.id}
                className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-pointer"
                onMouseEnter={() => setHoveredArticle(article.id)}
                onMouseLeave={() => setHoveredArticle(null)}
                onClick={() => navigate(`/blog/${article.id}`)}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-[#0066FF] text-white px-3 py-1 rounded-full text-xs font-medium">
                      {article.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-[#0A0A0A] mb-3 group-hover:text-[#0066FF] transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">{article.excerpt}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {article.tags.slice(0, 3).map((tag, index) => (
                      <span key={index} className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-3 text-gray-500 text-xs">
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        {article.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {article.readTime}
                      </span>
                    </div>
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center group-hover:bg-[#0066FF] transition-colors">
                      <i className={`ri-arrow-right-line text-sm ${hoveredArticle === article.id ? 'text-white' : 'text-gray-600'}`}></i>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center mt-16">
            <p className="text-gray-600 mb-6">
              Vous souhaitez collaborer ou discuter de projets techniques ?
            </p>
            <button
              onClick={() => navigate('/contact')}
              className="inline-flex items-center gap-2 bg-[#0066FF] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#0052CC] transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl hover:-translate-y-1"
            >
              <i className="ri-chat-3-line"></i>
              Discutons de votre projet
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
